// initial state
import docendo from '../../docendo'
import Vue from 'vue'
import eventbus from '../../eventbus'
import {computePreparationHoursByCalendar, getVHoursForSubject} from '../../utils/preparationFactor'

const state = {
  isSubmittingTask: false,
  isLoading: true,
  isLoadingSubjects: false,
  isLoadingTasks: false,
  isConfirmDeletion: false,
  itemToDelete: null,
  theDeletedItemId: null,
  subjects: [],
  usersAssignedHours: [],
  tasks: [],
  subjectTypeFilter: 'klass',
  selectedSubjectTypeFilters: [],
  subjectTypes: {
    da: [
      {name: 'Aktiviteter', value: 'activity', desc: 'Tæller UV-tid og reducerer timer på andre fag'},
      {name: 'Fag', value: 'subject', desc: 'Tæller timer for klassen. To samtidige brikker tæller halvt.'},
      {name: 'UUV', value: 'supplemental', desc: 'Tæller selvstændigt på understøttende undervisning.'},
      {
        name: 'Temavisning',
        value: 'frame',
        desc: 'Kan bruges til at markere temaer og anderledes dage i skemaet. Brikken er synlig for elever/forældre, mens fag-brikker, der ligger samtidig med tema-brikken, ikke er synlige. Fagbrikker tæller 100% i tidsrummet, og temabrikken tæller ikke.'
      },
      {name: 'Pause', value: 'break', desc: 'Andre lektioner bør ikke overlappe med en pause.'},
      {
        name: 'Ressource (skjult)',
        value: 'resource',
        desc: 'Bruges til at markere ressourcer i skemaet. Tæller ikke fra andre brikker. Vises som standard ikke i elev- og forældreskema.'
      },
      {
        name: 'Valgfag/Hold',
        value: 'optional',
        desc: 'Tæller 100 % og uafhængigt af andre brikker i samme tidsrum.'
      },
      {
        name: 'Ressource',
        value: 'person_resource',
        desc: 'Tæller UV-tid for underviseren. Tæller ikke ned fra andre fag samtidig i klassen. Vises ikke i elev- og forældre visning.'
      },
      {
        name: 'Ressource (synlig)',
        value: 'person_resource_visible',
        desc: 'Bruges til at markere ekstra ressurser i skemaet. Tæller ikke fra andre brikker. Vises i elev- og forældreskema.'
      },
      {name: 'Anden tid', value: 'other_tasks', desc: 'Ikke undervisning.'},
      {name: 'Øvrig tid', value: 'other_stuff', desc: 'Ikke undervisning, ikke elev-relateret.'},
      {name: 'SFO', value: 'club', desc: 'Pædagogisk tid'},
      {name: 'Øvrige opgaver', value: 'additional_tasks', desc: 'Tæller ikke UV'}
    ],
    en: [
      {
        name: 'Subject',
        value: 'subject',
        desc: 'Counting hours for the class. Two simultaneous activities count as halves.'
      },
      {
        name: 'Theme', value: 'frame',
        desc: 'Can be used for marking themes and special days in the class schedule. The themed activity is visible for the pupils and parents, while classes that are overlapping the themed activity will not be visible. Classes are fully counting, and the themed activity is not.'
      },
      {name: 'Break', value: 'break', desc: 'Subjects should not overlap with breaks.'},
      {
        name: 'Ressource', value: 'resource',
        desc: 'Can be used to register ressources. Is not visible to students/parents.'
      },
      {
        name: 'Optional classes/groups',
        value: 'optional',
        desc: 'Fully counting and independent from other activities in the same time period.'
      },
      {
        name: 'Ressource', value: 'resource',
        desc: 'Can be used to register ressources. Is not visible to students/parents.'
      },
      {name: 'Other tasks', value: 'other_tasks', desc: 'Not teaching.'},
      {name: 'Additional tasks', value: 'other_stuff', desc: 'Not classes and teaching, non-pupil related.'}
    ],
    nb: [
      {name: 'Fag', value: 'subject', desc: 'Teller timer for klassen'},
      {
        name: 'Temavisning',
        value: 'frame',
        desc: 'Kan brukes til å markere temaer og spesielle dager/uker i timeplanen. Temavisning er synlig for elever/foreldre, og kan kombineres med fag. Fag teller 100% i perioden, og temaet teller ikke.'
      },
      {name: 'Pause', value: 'break', desc: 'Andre timer bør ikke overlapper med en pause.'},
      {
        name: 'Ressurs',
        value: 'resource',
        desc: 'Brukes til å markere ressursene i timeplanen. Teller ikke fra andre fag. Vises som standard ikke i elev- og foreldretimeplan.'
      },
      {
        name: 'Valgfag/gruppe',
        value: 'optional',
        desc: 'Teller 100% og uavhengig av andre aktiviteter i samme tidsrom.'
      },
      {
        name: 'Ressurs',
        value: 'resource',
        desc: 'Brukes til å markere ressursene i timeplanen. Teller ikke fra andre fag. Vises som standard ikke i elev- og foreldretimeplan.'
      },
      {name: 'Annen tid', value: 'other_tasks', desc: 'Ikke undervisning.'},
      {name: 'Øvrig tid', value: 'other_stuff', desc: 'Ikke undervisning, ikke elevrelatert.'}
    ]
  },
  userTableAcc: {
    teaching: true
  },
  actualSubjectsForUser: [],
  isLoadingTasksForUser: true,
  userTasks: [],
  userActualHours: {},
  dbSubjectTypes: [
    {
      key: 'absence',
      i18n: {
        da: 'Fravær',
        en: 'Absence',
        nb: 'Fravær'
      }
    },
    {
      key: 'additional_tasks',
      i18n: {
        da: 'Øvrige opgaver',
        en: 'Additional tasks',
        nb: 'Ekstra oppgaver'
      }
    },
    {
      key: 'activity',
      i18n: {
        da: 'Aktivitet',
        en: 'Activity',
        nb: 'Aktivitet'
      }
    },
    {
      key: 'available',
      i18n: {
        da: 'Rådighedstime',
        en: 'Available',
        nb: 'Tilgjengelig'
      }
    },
    {
      key: 'break',
      i18n: {
        da: 'Pause',
        en: 'Break',
        nb: 'Pause'
      }
    },
    {
      key: 'club',
      i18n: {
        da: 'SFO',
        en: 'Club',
        nb: 'Klubb'
      }
    },
    {
      key: 'exercise',
      i18n: {
        da: 'Bevægelse',
        en: 'Exercise',
        nb: 'Trening'
      }
    },
    {
      key: 'flexin',
      i18n: {
        da: 'Flex ind',
        en: 'Flex in',
        nb: 'Flex inn'
      }
    },
    {
      key: 'flexout',
      i18n: {
        da: 'Flex ud',
        en: 'Flex out',
        nb: 'Flex ut'
      }
    },
    {
      key: 'frame',
      i18n: {
        da: 'Temavisning',
        en: 'Frame',
        nb: 'Temavisning'
      }
    },
    {
      key: 'optional',
      i18n: {
        da: 'Valgfag',
        en: 'Optional',
        nb: 'Valgfritt'
      }
    },
    {
      key: 'other_stuff',
      i18n: {
        da: 'Øvrig tid',
        en: 'Other stuff',
        nb: 'Andre ting'
      }
    },
    {
      key: 'other_tasks',
      i18n: {
        da: 'Anden tid',
        en: 'Other tasks',
        nb: 'Annen tid'
      }
    },
    {
      key: 'resource',
      i18n: {
        da: 'Ressource (skjult)',
        en: 'Resource',
        nb: 'Ressurs'
      }
    },
    {
      key: 'person_resource',
      i18n: {
        da: 'Person ressource (skjult)',
        en: 'Person resource',
        nb: 'Personressurs'
      }
    },
    {
      key: 'person_resource_visible',
      i18n: {
        da: 'Ressource (synlig)',
        en: 'Visible person resource',
        nb: 'Synlig personressurs'
      }
    },
    {
      key: 'pool',
      i18n: {
        da: 'Lokalpulje',
        en: 'Pool',
        nb: 'Pulje'
      }
    },
    {
      key: 'preparation',
      i18n: {
        da: 'Forberedelse',
        en: 'Preparation',
        nb: 'Forberedelse'
      }
    },
    {
      key: 'reservation',
      i18n: {
        da: 'Reservation',
        en: 'Reservation',
        nb: 'Reservasjon'
      }
    },
    {
      key: 'subject',
      i18n: {
        da: 'Fag',
        en: 'Subject',
        nb: 'Fag'
      }
    },
    {
      key: 'supplemental',
      i18n: {
        da: 'UUV',
        en: 'Supplemental',
        nb: 'Supplerende'
      }
    },
    {
      key: 'with_kids',
      i18n: {
        da: 'Med børn',
        en: 'With kids',
        nb: 'Med barn'
      }
    },
    {
      key: 'without_kids',
      i18n: {
        da: 'Uden børn',
        en: 'Without kids',
        nb: 'Uten barn'
      }
    }
  ],
  subjectsRequests: [],
  isLoadingSubjectsRequests: false,
  reductions: [],
  isLoadingReductions: false
}

const getters = {
  reductionsInSubjectIdHash (state, getters) {
    const obj = {}
    state.reductions.forEach(reduction => {
      const activity = getters.subjectsInHash[reduction.activity_id] || {}
      const reductionWithActivity = {
        ...reduction, // undgå mutation af state direkte
        vActivity: activity,
        vActivityTitle: activity.name || '',
        vActivityReductionHours: activity.hours || '?'
      }

      if (!obj[reduction.subject_id]) {
        obj[reduction.subject_id] = [reductionWithActivity]
      } else {
        obj[reduction.subject_id].push(reductionWithActivity)
      }
    })
    return obj
  },
  activeSubjectTypeFilters (state, getters) {
    const typesI18n = state.subjectTypes[getters.locale]
    return state.selectedSubjectTypeFilters
      .filter(filter => typesI18n.some(type => type.name === filter))
      .map(filter => {
        return typesI18n.find(type => type.name === filter).value
      })
  },
  subjectsFilteredOnType (state, getters) {
    const activeFilters = getters.activeSubjectTypeFilters

    if (!activeFilters || activeFilters.length === 0) {
      return state.subjects
    }

    return state.subjects.filter(subject => {
      return activeFilters.includes(subject.subject_type)
    })
  },
  klassCalIdsHashSortedOnName (state, getters, rootState, rootGetters) {
    const klasses = rootGetters.klassCalendars
    return klasses.map(cal => cal.id)
  },
  subjectsInKlassHash (state, getters, rootState, rootGetters) {
    let klassCalsWithSubjectIds = {}
    state.subjects.forEach(subject => {
      subject.calendar_ids.forEach(calId => {
        if (rootGetters.allKlassCalendarsInHash[calId]) {
          if (!klassCalsWithSubjectIds[calId]) {
            klassCalsWithSubjectIds[calId] = [subject.id]
          } else {
            klassCalsWithSubjectIds[calId].push(subject.id)
          }
        }
      })
    })
    return klassCalsWithSubjectIds
  },
  subjectsInCalendarHash (state, getters, rootState, rootGetters) {
    let calsWithSubjectIds = {}
    state.subjects.forEach(subject => {
      let calId = subject.calendar_id
      if (!calsWithSubjectIds[calId]) {
        calsWithSubjectIds[calId] = [subject.id]
      } else {
        calsWithSubjectIds[calId].push(subject.id)
      }
    })
    return calsWithSubjectIds
  },
  subjectsInUserHash (state, getters, rootState, rootGetters) {
    let calIdsWithSubjectIds = {}
    state.subjects.forEach(subject => {
      if (subject.master_type === 'teaching') {
        subject.calendar_ids.forEach(calid => {
          if (calid && rootGetters.allUsersInCalIdHash[calid]) {
            if (!calIdsWithSubjectIds[calid]) {
              calIdsWithSubjectIds[calid] = [subject.id]
              // If not already in array (remove duplicate)
            } else if (calIdsWithSubjectIds[calid].indexOf(subject.id) < 0) {
              calIdsWithSubjectIds[calid].push(subject.id)
            }
          }
        })
      }
    })
    return calIdsWithSubjectIds
  },
  actualSubjectsForUserInHash (state) {
    let subjects = state.actualSubjectsForUser.reduce((obj, item) => {
      obj[item.id] = item
      return obj
    }, {})
    return subjects
  },
  subjectsInHash (state, getters) {
    return state.subjects.reduce((obj, item) => {
      obj[item.id] = item
      return obj
    }, {})
  },
  usersAssignedHoursHash (state) {
    if (!state.usersAssignedHours.length || state.usersAssignedHours.length < 1) {
      return {}
    }
    return Object.assign({}, ...state.usersAssignedHours.map(item => ({[item.user_id]: item})))
  },
  uniqueSubjects (state, getters, rootState, rootGetters) {
    if (state.isLoading || rootGetters.klassCalendars.length === 0) {
      return []
    }

    const klassCalendars = rootGetters.klassCalendars
    const klassCalendarsInHash = rootGetters.allKlassCalendarsInHash
    const allCalendarsInHash = rootGetters.allCalendarsInHash

    const subjectsMap = {}

    getters.subjectsFilteredOnType.forEach(subject => {
      const calendar = allCalendarsInHash[subject.calendar_id]

      if (!calendar || calendar.type !== state.subjectTypeFilter) return

      const groupKey = subject.name + '|' + subject.subject_type

      if (!subjectsMap[groupKey]) {
        subjectsMap[groupKey] = {
          ...subject,
          klasses: klassCalendars.reduce((obj, klass) => {
            obj[klass.id] = {calendar_id: klass.id}
            return obj
          }, {})
        }
      }

      subject.calendar_ids.forEach(calId => {
        if (klassCalendarsInHash[calId]) {
          subjectsMap[groupKey].klasses[calId] = subject
        }
      })
    })

    // Lav om til array og sorter
    return Object.values(subjectsMap).sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    )
  },
  tasksInUserHash (state) {
    let calIdsWithTasksIds = {}
    state.tasks.forEach(task => {
      if (task.user_calendar_ids) {
        task.user_calendar_ids.forEach(calid => {
          if (calid) {
            if (!calIdsWithTasksIds[calid]) {
              calIdsWithTasksIds[calid] = [task.id]
            } else {
              calIdsWithTasksIds[calid].push(task.id)
            }
          }
        })
      }
    })
    return calIdsWithTasksIds
  },
  tasksInHash (state, getters) {
    state.tasks.forEach(task => {
      const teaching = task.subject_type === 'person_resource' || task.subject_type === 'person_resource_visible' || task.subject_type === 'subject' ? task.hours : null
      const tasks = task.subject_type === 'additional_tasks' ? task.hours : null
      const prep = task.subject_type === 'preparation' ? task.hours : null
      const pool = task.subject_type === 'pool' ? task.hours : null
      Vue.set(task, 'timeTeaching', teaching)
      Vue.set(task, 'timeTasks', tasks)
      Vue.set(task, 'timePrep', prep)
      Vue.set(task, 'timePool', pool)
    })
    let tasks = state.tasks.reduce((obj, item) => {
      obj[item.id] = item
      return obj
    }, {})
    return tasks
  },
  userTasksSortedInTypes () {
    let selectSorting = {
      teaching: 1,
      substitute_hours: 2,
      club: 3,
      other: 4,
      preparation: 5,
      pool: 6,
      absence: 10,
      with_kids: 1,
      without_kids: 2
    }
    let types = []
    state.userTasks.forEach(task => {
      if (!task.actual && ((task.hours * 1) === 0)) {
        let arrOfSubjects = state.userTasks.filter(allTask => allTask.id === task.id)
        // remove if task already exists as a substitute hour && actual == 0 && hours == 0
        if (arrOfSubjects.length > 1) {
          return
        }
      }
      let activeType = task.task_category_name ? task.task_category_name : task.master_type // task.subject_type === 'with_kids' || task.subject_type === 'without_kids' ? task.subject_type : task.master_type
      if (task.substitute) {
        activeType = 'substitute_hours'
      }
      let idx = types.findIndex(type => type.title === activeType)
      if (idx > -1) {
        types[idx].tasks.push(task)
      } else {
        const order = (task.hasOwnProperty('task_category_rank') && task.task_category_rank !== null)
          ? task.task_category_rank
          : (selectSorting[activeType] !== undefined)
            ? selectSorting[activeType]
            : 100
        let obj = {
          title: activeType,
          order: order,
          tasks: [task]
        }
        types.push(obj)
      }
    })
    // Same rank: named task categories before uncategorized master_type groups
    // (e.g. "Undervisning" before activities that fall back to "teaching")
    return types.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order
      const aIsMasterType = Object.prototype.hasOwnProperty.call(selectSorting, a.title)
      const bIsMasterType = Object.prototype.hasOwnProperty.call(selectSorting, b.title)
      if (aIsMasterType !== bIsMasterType) return aIsMasterType ? 1 : -1
      return 0
    })
  },
  subjectsRequestsInHash (state, getters) {
    const obj = {}
    state.subjectsRequests.forEach(req => {
      if (obj[req.subject_id]) {
        obj[req.subject_id].push(req.calendar_id)
      } else {
        obj[req.subject_id] = [req.calendar_id]
      }
    })
    return obj
  },
  teachingHoursPerUser (state, getters, rootState, rootGetters) {
    const getVHours = (subject, userCalId) => {
      if (!subject) return 0

      // 1. Brugerens egen kalender
      if (Array.isArray(subject.subject_calendars)) {
        const userMatch = subject.subject_calendars.find(sc =>
          sc.calendar_id === userCalId && sc.hours != null
        )
        if (userMatch) {
          const parsed = parseFloat(userMatch.hours)
          return isNaN(parsed) ? 0 : parsed
        }
      }

      // 2. Første klass med timer
      if (Array.isArray(subject.cals)) {
        const klass = subject.cals.find(c => c.calendar_type === 'klass')
        if (klass && Array.isArray(subject.subject_calendars)) {
          const klassMatch = subject.subject_calendars.find(sc =>
            sc.calendar_id === klass.id && sc.hours != null
          )
          if (klassMatch) {
            const parsed = parseFloat(klassMatch.hours)
            return isNaN(parsed) ? 0 : parsed
          }
        }
      }

      // 3. Fallback
      const fallback = parseFloat(subject.hours)
      return isNaN(fallback) ? 0 : fallback
    }

    const userTeachingHours = {}

    state.subjects.forEach(subject => {
      if (subject.master_type === 'teaching' && subject.task_overview !== false && Array.isArray(subject.cals)) {
        subject.cals.forEach(calendar => {
          if (calendar.calendar_type === 'user') {
            const calId = calendar.id
            if (!userTeachingHours[calId]) {
              userTeachingHours[calId] = 0
            }

            const hours = getVHours(subject, calId)
            userTeachingHours[calId] += hours
          }
        })
      }
    })

    return userTeachingHours
  },
  taskHoursPerUser (state, getters, rootState, rootGetters) {
    const getSubjectHours = (subject, calId) => {
      const userId = rootGetters.allUsersInCalIdHash[calId] ? rootGetters.allUsersInCalIdHash[calId]['id'] : null
      if (!userId) {
        subject.hours * 1
      }
      let idx = subject.teachers[0] ? subject.teachers.findIndex(teacher => teacher.user_id === userId) : -1
      if (idx > -1) {
        return subject.teachers[idx].hours * 1
      } else {
        return subject.hours * 1
      }
    }
    let userHours = {}
    state.tasks.forEach(subject => {
      if (subject.master_type === 'teaching') {
        subject.cals.forEach(calendar => {
          if (calendar.calendar_type === 'user') {
            const calId = calendar.id
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            const hours = getSubjectHours(subject, calId)
            userHours[calId] += hours || 0
          }
        })
      }
    })
    return userHours
  },
  tasksHoursInUserHash (state) {
    const subjectType = 'additional_tasks'
    let userHours = {}
    state.tasks.forEach(task => {
      if (task.user_calendar_ids && task.subject_type === subjectType) {
        task.user_calendar_ids.forEach(calId => {
          if (calId) {
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            const hours = task.hours
            userHours[calId] += hours || 0
          }
        })
      }
    })
    state.subjects.forEach(subject => {
      if (subject.subject_type === subjectType && subject.task_overview !== false && Array.isArray(subject.cals)) {
        subject.cals.forEach(calendar => {
          if (calendar.calendar_type === 'user') {
            const calId = calendar.id
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            userHours[calId] += getVHoursForSubject(subject, calId)
          }
        })
      }
    })
    return userHours
  },
  preparationHoursInUserHash (state) {
    const subjectType = 'preparation'
    let userHours = {}
    state.tasks.forEach(task => {
      if (task.user_calendar_ids && task.subject_type === subjectType) {
        task.user_calendar_ids.forEach(calId => {
          if (calId) {
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            const hours = task.hours
            userHours[calId] += hours || 0
          }
        })
      }
    })
    state.subjects.forEach(subject => {
      if (subject.subject_type === subjectType && subject.task_overview !== false && Array.isArray(subject.cals)) {
        subject.cals.forEach(calendar => {
          if (calendar.calendar_type === 'user') {
            const calId = calendar.id
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            userHours[calId] += getVHoursForSubject(subject, calId)
          }
        })
      }
    })
    return userHours
  },
  localPoolHoursInUserHash (state) {
    const subjectType = 'pool'
    let userHours = {}
    state.tasks.forEach(task => {
      if (task.user_calendar_ids && task.subject_type === subjectType) {
        task.user_calendar_ids.forEach(calId => {
          if (calId) {
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            const hours = task.hours
            userHours[calId] += hours || 0
          }
        })
      }
    })
    state.subjects.forEach(subject => {
      if (subject.subject_type === subjectType && subject.task_overview !== false && Array.isArray(subject.cals)) {
        subject.cals.forEach(calendar => {
          if (calendar.calendar_type === 'user') {
            const calId = calendar.id
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            userHours[calId] += getVHoursForSubject(subject, calId)
          }
        })
      }
    })
    return userHours
  },
  clubHoursInUserHash (state) {
    const subjectType = 'club'
    let userHours = {}
    state.tasks.forEach(task => {
      if (task.user_calendars && task.subject_type === subjectType) {
        task.user_calendars.forEach(calId => {
          if (calId) {
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            const hours = task.hours
            userHours[calId] += hours || 0
          }
        })
      }
    })
    state.subjects.forEach(subject => {
      if (subject.subject_type === subjectType && subject.task_overview !== false && Array.isArray(subject.cals)) {
        subject.cals.forEach(calendar => {
          if (calendar.calendar_type === 'user') {
            const calId = calendar.id
            if (!userHours[calId]) {
              userHours[calId] = 0
            }
            userHours[calId] += getVHoursForSubject(subject, calId)
          }
        })
      }
    })
    return userHours
  },
  /**
   * Kalender-id -> vægtet forberedelse (DK + deactivatePools + prepFactor), ellers null.
   */
  weightedPreparationHoursForCalendarId (state, getters) {
    const weighted = getters.preparationHoursByCalendar.weighted
    return function (calId) {
      if (!weighted) {
        return null
      }
      const value = weighted[Number(calId)]
      return value === undefined ? null : value
    }
  },
  /**
   * Kalender-id -> sum af timer fra forberedelsesopgaver med eksplicit preparation-faktor.
   */
  explicitPrepTaskHoursForCalendarId (state, getters) {
    const explicit = getters.preparationHoursByCalendar.explicit
    return function (calId) {
      return explicit[Number(calId)] || 0
    }
  },
  preparationHoursByCalendar (state, getters, rootState, rootGetters) {
    return computePreparationHoursByCalendar(state, rootGetters, rootState)
  }
}

const mutations = {
  SET_SUBJECT_TYPE_FILTER (state, subjectType) {
    state.selectedSubjectTypeFilters = subjectType
  },
  TOGGLE_SUBJECT_TYPE_FILTER (state, filter) {
    const index = state.selectedSubjectTypeFilters.indexOf(filter)
    if (index === -1) {
      state.selectedSubjectTypeFilters.push(filter)
    } else {
      state.selectedSubjectTypeFilters.splice(index, 1)
    }
  },
  CLEAR_SUBJECT_TYPE_FILTERS (state) {
    state.selectedSubjectTypeFilters = []
  },
  addSubjectRequest (state, payload) {
    state.subjectsRequests.push(payload)
  },
  removeSubjectRequest (state, payload) {
    const idx = state.subjectsRequests.findIndex(request => request.id === payload.id)
    if (idx > -1) {
      state.subjectsRequests.splice(idx, 1)
    }
  },
  setUserTableAcc (state, accOpen) {
    Vue.set(state.userTableAcc, accOpen.title, accOpen.boolean)
  },
  setTheDeletedItemId (state, itemId) {
    state.theDeletedItemId = itemId
  },
  setItemToDelete (state, item) {
    state.itemToDelete = item
  },
  setSubjects (state, data) {
    state.subjects = data
  },
  createSubject (state, newSubject) {
    Vue.set(state.subjects, state.subjects.length, newSubject)
  },
  updateSubject (state, updatedSubject) {
    const idx = state.subjects.findIndex(subject => subject.id === updatedSubject.id)
    Vue.set(state.subjects, idx, updatedSubject)
  },
  deleteSubject (state, deletedSubject) {
    const idx = state.subjects.findIndex(subject => subject.id === deletedSubject.id)
    state.subjects.splice(idx, 1)
  },
  setUsersAssignedHours (state, data) {
    state.usersAssignedHours = data
  },
  createTask (state, newTask) {
    Vue.set(state.tasks, state.tasks.length, newTask)
  },
  setTasks (state, data) {
    state.tasks = data
  },
  updateTask (state, updatedTask) {
    let idx = state.tasks.findIndex(task => task.id === updatedTask.oldId)
    Vue.set(state.tasks, idx, updatedTask)
  },
  setTaskHours (state, { taskId, hours }) {
    const task = state.tasks.find(t => t.id === taskId)
    if (task) {
      Vue.set(task, 'hours', hours)
    }
  },
  deleteTask (state, deletedTask) {
    let idx = state.tasks.findIndex(task => task.id === deletedTask.id)
    state.tasks.splice(idx, 1)
  },
  setConfirmDeletion (state, boolean) {
    state.isConfirmDeletion = boolean
  },
  updateTeacherHoursOnSubject (state, obj) {
    const subjectIdx = state.subjects.findIndex(function (subject) {
      return subject.id === obj.subject_id
    })

    if (subjectIdx === -1) {
      return
    }

    const subject = state.subjects[subjectIdx]

    // Sikrer at felter eksisterer
    if (!subject.teachers) subject.teachers = []
    if (!subject.subject_calendars) subject.subject_calendars = []
    if (!subject.calendar_ids) subject.calendar_ids = []

    // ---- TEACHERS ----
    let teacherIdx = -1
    for (let i = 0; i < subject.teachers.length; i++) {
      if (subject.teachers[i] && subject.teachers[i].user_id === obj.user_id) {
        teacherIdx = i
        break
      }
    }

    if (teacherIdx > -1) {
      // Opdater eksisterende lærer
      Vue.set(subject.teachers, teacherIdx, obj)
    } else {
      // Tilføj ny lærer (håndtér [null] kanttilfældet)
      if (subject.teachers.length === 1 && subject.teachers[0] === null) {
        Vue.set(subject.teachers, 0, obj)
      } else {
        subject.teachers.push(obj)
      }
    }

    // ---- SUBJECT_CALENDARS ----
    // Find kalenderen i subject_calendars via calendar_id
    let scIdx = -1
    for (let i = 0; i < subject.subject_calendars.length; i++) {
      const sc = subject.subject_calendars[i]
      if (sc && sc.calendar_id === obj.calendar_id) {
        scIdx = i
        break
      }
    }

    if (scIdx > -1) {
      // Opdater eksisterende kalenderpost
      const updated = Object.assign({}, subject.subject_calendars[scIdx], {
        calendar_id: obj.calendar_id,
        hours: obj.hours
      })
      Vue.set(subject.subject_calendars, scIdx, updated)
    } else {
      // Tilføj ny kalenderpost
      subject.subject_calendars.push({
        calendar_id: obj.calendar_id,
        hours: obj.hours
      })
    }

    // ---- CALENDAR_IDS ----
    // Sørg for at calendar_ids indeholder calendar_id
    let hasCalId = false
    for (let i = 0; i < subject.calendar_ids.length; i++) {
      if (subject.calendar_ids[i] === obj.calendar_id) {
        hasCalId = true
        break
      }
    }
    if (!hasCalId) {
      subject.calendar_ids.push(obj.calendar_id)
    }

    // Sæt det opdaterede subject tilbage i arrayet for at sikre reaktivitet
    Vue.set(state.subjects, subjectIdx, subject)
  },
  setActualSubjectsForUser (state, arr) {
    state.actualSubjectsForUser = arr
  },
  setUserTasks (state, arr) {
    const filterOutReservations = arr.filter(item => item.subject_type !== 'reservation')
    state.userTasks = filterOutReservations
  },
  updateUserTaskHours (state, obj) {
    state.userTasks.forEach(task => {
      if (task.id === obj.id && !task.substitute) {
        const actual = task.actual ? task.actual * 1 : 0
        Vue.set(task, 'hours', obj.hours)
        Vue.set(task, 'diff', obj.hours ? +(obj.hours - actual).toFixed(2) : '')
      }
    })
  },
  setUserTaskSubjectCalendarId (state, obj) {
    state.userTasks.forEach(task => {
      if (task.id === obj.id && !task.substitute) {
        Vue.set(task, 'subject_calendar_id', obj.subjectCalendarId)
      }
    })
  },
  setUsersActualHours (state, arr) {
    state.userActualHours = arr[0]
  }
}

const actions = {
  readReductions (context, obj = {}) {
    context.state.isLoadingReductions = true
    const schoolId = obj.schoolId || context.getters.schoolId
    const schoolYearId = obj.schoolYearId || context.getters.schoolYearId
    docendo.listSubjectReductions(schoolId, schoolYearId, success => {
      context.state.reductions = success
      context.state.isLoadingReductions = false
    }, failure => {
      console.log({failure})
    })
  },
  readUserTasks (context, obj) {
    if (!obj.isSilent) {
      context.state.isLoadingTasksForUser = true
    }
    docendo.userTasks(obj.userId, obj.fromDate, obj.toDate, null, data => {
      if (data && data.status && data.status === 'error') {
        eventbus.toast = {text: data.message, 'success': false}
        return
      }
      context.state.isLoadingTasksForUser = false
      context.commit('setUserTasks', data)
    }, failure => {
      console.log('failure', failure)
    })
  },
  readTasksForUser: (context, objWithSchoolYearIdAndUserId) => {
    context.state.isLoadingTasksForUser = true
    docendo.readTasks(window.SCHOOL.id, objWithSchoolYearIdAndUserId.activeSchoolYearId, objWithSchoolYearIdAndUserId.userId, data => {
      context.state.isLoadingTasksForUser = false
      context.commit('setTasks', data)
    }, failure => {
      console.log('failed read tasks')
    })
  },
  readSubjectsForUser: (context, objWithSchoolYearIdAndUserId) => {
    context.state.isLoading = true
    docendo.readSubjects(window.SCHOOL.id, objWithSchoolYearIdAndUserId.activeSchoolYearId, objWithSchoolYearIdAndUserId.userId, data => {
      state.isLoading = false
      context.commit('setSubjects', data)
    }, failure => {
      console.log('failed read subjects')
    })
  },
  readActualSubjectsForUser: (context, objWithSchoolYearIdAndUserId) => {
    context.state.isLoadingActualUserHours = true
    docendo.subjectActuals(window.SCHOOL.id, objWithSchoolYearIdAndUserId.activeSchoolYearId, objWithSchoolYearIdAndUserId.userId, data => {
      context.state.isLoadingActualUserHours = false
      context.commit('setActualSubjectsForUser', data)
    }, failure => {
      console.log('failed read subjects')
    })
  },
  readUsersAssignedHoursForUser: (context, schoolObj) => {
    docendo.schoolUserYearsList(schoolObj.schoolId, schoolObj.schoolYearId, schoolObj.userId, data => {
      context.commit('setUsersAssignedHours', data)
    }, failure => {
      console.log('failure updated Topics')
    })
  },
  readUsersActualHoursForUser: (context, schoolObj) => {
    docendo.schoolActualHours(schoolObj.schoolId, schoolObj.fromDateTime, schoolObj.toDateTime, [schoolObj.userId], data => {
      context.commit('setUsersActualHours', data)
    }, failure => {
      console.log('failure updated Topics')
    })
  },
  readSubjects: (context, activeSchoolYearId) => {
    context.state.isLoading = true
    context.state.isLoadingSubjects = true
    const schoolId = context.getters.schoolId
    return new Promise(resolve => {
      docendo.readSubjects(schoolId, activeSchoolYearId, null, data => {
        context.state.isLoading = false
        context.state.isLoadingSubjects = false
        context.commit('setSubjects', data)
        resolve(data)
      }, failure => {
        context.state.isLoading = false
        context.state.isLoadingSubjects = false
        console.log('failed read subjects')
        resolve(null)
      })
    })
  },
  createSubject: (context, subject) => {
    const schoolId = subject.schoolId ? subject.schoolId : window.SCHOOL.id
    const preparation = subject.preparation != null && subject.preparation !== '' ? subject.preparation : null
    docendo.createSubject(subject.name, subject.color, subject.calendarId, subject.defaultDuration, preparation, subject.hours, subject.subjectType, schoolId, subject.schoolYearId, subject.calendarIds, subject.subjectCode, false, subject.fromDate, subject.toDate, subject.taskOverview, data => {
      context.commit('createSubject', data)
      // Native window event - Talk to ROR
      data['calCurrentdate'] = subject.calCurrentdate
      window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: data}))
    }, failure => {
      console.log('failure')
    })
  },
  updateSubject: (context, updatedSubject) => {
    const away = updatedSubject.away ? updatedSubject.away : false
    const preparation = updatedSubject.preparation != null && updatedSubject.preparation !== '' ? updatedSubject.preparation : null
    docendo.updateSubject(updatedSubject.id, updatedSubject.name, updatedSubject.color, updatedSubject.calendarId, updatedSubject.calendarIds, updatedSubject.defaultDuration, preparation, updatedSubject.hours, updatedSubject.subjectType, updatedSubject.schoolId, updatedSubject.schoolYearId, updatedSubject.subjectCode, away, false, updatedSubject.fromDate, updatedSubject.toDate, updatedSubject.taskOverview, data => {
      context.commit('updateSubject', data)
      window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: data}))
    }, failure => {
      console.log('failure')
    })
  },
  deleteSubject: (context, deleteSubject) => {
    docendo.deleteSubject(deleteSubject.id, deleteSubject.school_id, deleteSubject.school_year_id, data => {
      context.commit('deleteSubject', data)
      window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: data}))
    }, failure => {
      console.log('failure updated Topics')
    })
  },
  readUsersAssignedHours: (context, schoolObj) => {
    docendo.schoolUserYearsList(schoolObj.schoolId, schoolObj.schoolYearId, null, data => {
      context.commit('setUsersAssignedHours', data)
    }, failure => {
      console.log('failure updated Topics')
    })
  },
  readTasks: (context, activeSchoolYearId) => {
    const schoolId = context.getters.schoolId
    context.state.isLoading = true
    context.state.isLoadingTasks = true
    docendo.readTasks(schoolId, activeSchoolYearId, null, data => {
      state.isLoading = false
      context.commit('setTasks', data)
      context.state.isLoadingTasks = false
      window.dispatchEvent(new window.CustomEvent('vueDoneLoadingTasks', {detail: data}))
    }, failure => {
      console.log('failed read tasks')
    })
  },
  reloadTasks: (context, activeSchoolYearId) => {
    const schoolId = context.getters.schoolId
    context.state.isSubmittingTask = true
    return new Promise(resolve => {
      docendo.readTasks(schoolId, activeSchoolYearId, null, data => {
        context.commit('setTasks', data)
        context.state.isSubmittingTask = false
        resolve(data)
      }, failure => {
        context.state.isSubmittingTask = false
        console.log('failed reload tasks')
        resolve(null)
      })
      docendo.readSubjects(schoolId, activeSchoolYearId, null, data => {
        context.commit('setSubjects', data)
      }, failure => {
        console.log('failed reload subjects')
      })
    })
  },
  createTask: (context, task) => {
    context.state.isSubmittingTask = true
    const preparation = task.preparation != null && task.preparation !== '' ? task.preparation * 1 : null
    const schoolId = context.getters.schoolId
    docendo.createTask(schoolId, task.schoolYearId, task.subjectType, task.hours, task.name, task.color, task.defaultDuration, preparation, task.belongsToCalendars, task.userCalendars, task.canSchedule, task.inUserCalendar, task.fromDate, task.toDate, data => {
      if (data && data.status === 'error') {
        console.warn('createTask backend error:', data.message)
        eventbus.toast = {text: 'Kunne ikke oprette opgaven - prøv igen', success: false}
        context.state.isSubmittingTask = false
        context.dispatch('reloadTasks', task.schoolYearId)
        return
      }
      context.commit('createTask', data)
      context.state.isSubmittingTask = false
      context.dispatch('reloadTasks', task.schoolYearId)
    }, failure => {
      console.warn('createTask failure', failure)
      eventbus.toast = {text: 'Kunne ikke oprette opgaven - prøv igen', success: false}
      context.state.isSubmittingTask = false
      context.dispatch('reloadTasks', task.schoolYearId)
    })
  },
  updateTask: (context, task) => {
    context.state.isSubmittingTask = true
    const oldTaskId = task.id
    const preparation = task.preparation != null && task.preparation !== '' ? task.preparation * 1 : null
    docendo.updateTask(task.schoolId, task.schoolYearId, task.id, task.name, task.color, task.hours, task.defaultDuration, preparation, task.belongsToCalendars, task.userCalendars, task.inUserCalendar, task.fromDate, task.toDate, data => {
      if (data && data.status === 'error') {
        console.warn('updateTask backend error:', data.message)
        eventbus.toast = {text: 'Kunne ikke gemme opgaven - prøv igen', success: false}
        context.state.isSubmittingTask = false
        context.dispatch('reloadTasks', task.schoolYearId)
        return
      }
      const updated = data
      updated['oldId'] = oldTaskId
      context.commit('updateTask', updated)
      context.state.isSubmittingTask = false
      context.dispatch('reloadTasks', updated.school_year_id)
    }, failure => {
      console.warn('updateTask failure', failure)
      eventbus.toast = {text: 'Kunne ikke gemme opgaven - prøv igen', success: false}
      context.state.isSubmittingTask = false
      context.dispatch('reloadTasks', task.schoolYearId)
    })
  },
  deleteTask: (context, task) => {
    const subjectIds = task.subject_ids || []
    if (subjectIds.length === 0) {
      context.commit('deleteTask', {id: task.id})
      return
    }
    const refreshSubjects = () => {
      docendo.readSubjects(task.school_id, task.school_year_id, null, subjects => {
        context.commit('setSubjects', subjects)
      }, () => {})
    }
    docendo.bulkDeleteSubjects(task.school_id, subjectIds, data => {
      const result = typeof data === 'string' ? JSON.parse(data) : data
      if (result && Array.isArray(result.deleted)) {
        result.deleted.forEach(subj => {
          context.commit('deleteSubject', subj)
          window.dispatchEvent(new window.CustomEvent('vueCRUDSubject', {detail: subj}))
        })
      }
      const hasErrors = result && Array.isArray(result.errors) && result.errors.length > 0
      if (hasErrors) {
        eventbus.toast = {text: 'Nogle brikker kunne ikke slettes pga. skemalagte begivenheder', success: false}
      } else {
        context.commit('deleteTask', {id: task.id})
      }
      refreshSubjects()
    }, failure => {
      console.warn('deleteTask failure', failure)
      eventbus.toast = {text: 'Kunne ikke slette opgaven - prøv igen', success: false}
      refreshSubjects()
    })
  },
  readSubjectRequests: (context, activeSchoolYearId) => {
    const schoolId = context.getters.schoolId
    context.state.isLoadingSubjectsRequests = true
    docendo.listSubjectRequests(schoolId, activeSchoolYearId, data => {
      if (data.status !== 'error') {
        context.state.subjectsRequests = data
      }
      context.state.isLoadingSubjectsRequests = false
    }, failure => {
      console.log('failed read tasks')
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/subjects.js